import { Check, ChevronLeft, SearchMd, XClose } from "@untitled-ui/icons-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroller";

import Button from "@/components/forms/Button";
import Checkbox from "@/components/forms/Checkbox";
import Input from "@/components/forms/Input";
import Loading from "@/components/loadings/Loading";
import UserAvatar from "@/components/media/UserAvatar";
import type { User } from "@/types";
import { useFetchUsersInfinite } from "@/user/services";
import { getNameGroupLabel } from "@/utils";

import AddUsersConfirmModal from "./AddUsersConfirmModal";
import { GroupUserRowSkeleton } from "./GroupUserListSkeleton";

type GroupAddUsersPageProps = {
  participantIds: Set<number>;
  onBack: () => void;
  onConfirm: (userIds: number[]) => void;
  isSubmitting?: boolean;
};

export default function GroupAddUsersPage({
  participantIds,
  onBack,
  onConfirm,
  isSubmitting,
}: GroupAddUsersPageProps) {
  const { t } = useTranslation("messenger.user");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const {
    data: users,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
  } = useFetchUsersInfinite({
    query: searchQuery,
    order: "first_name",
    direction: "asc",
  });

  const handleToggleUser = (userId: number) => {
    setSelectedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
        setSearchQuery("");
      }
      return next;
    });
  };

  const selectedUserList = useMemo(() => {
    const flatUsers = users?.pages?.flatMap((page) => page.data) ?? [];
    return flatUsers.filter((user) => selectedUsers.has(user.id));
  }, [users?.pages, selectedUsers]);

  const handleConfirm = () => {
    onConfirm(Array.from(selectedUsers));
    setShowConfirmModal(false);
  };

  let prevLetter = "";

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="px-6">
        <div className="flex items-center gap-3 py-4">
          <Button
            color="tertiary-gray"
            square
            onClick={() => {
              if (showSearch) {
                setShowSearch(false);
                setSearchQuery("");
              } else {
                onBack();
                setSelectedUsers(new Set());
              }
            }}
          >
            {showSearch ? (
              <XClose className="size-5 text-gray-700" />
            ) : (
              <ChevronLeft className="size-5 text-gray-700 rtl:rotate-180" />
            )}
          </Button>
          {showSearch ? (
            <Input
              startIcon={<SearchMd className="size-5" />}
              placeholder={t("Search", { ns: "core.shared" })}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="min-w-0 flex-1"
              autoFocus
            />
          ) : (
            <span className="min-w-0 flex-1 text-lg font-semibold text-gray-900">
              {selectedUsers.size > 0
                ? t("{{count}} users selected", { count: selectedUsers.size })
                : t("Add Users")}
            </span>
          )}
          {!showSearch && (
            <div className="flex items-center gap-3">
              <Button
                color="tertiary-gray"
                size="lg"
                square
                onClick={() => setShowSearch(true)}
              >
                <SearchMd className="size-5 text-gray-600" />
              </Button>
              {selectedUsers.size > 0 && (
                <Button
                  color="tertiary-gray"
                  size="lg"
                  square
                  onClick={() => setShowConfirmModal(true)}
                >
                  <Check className="size-5 text-gray-600" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          [...Array(6)].map((_, index) => <GroupUserRowSkeleton key={index} />)
        ) : (
          <InfiniteScroll
            pageStart={0}
            loadMore={() => !isFetchingNextPage && fetchNextPage()}
            hasMore={hasNextPage}
            useWindow={false}
            className="flex flex-col"
            loader={<Loading key={0} className="my-2 self-center" size="sm" />}
          >
            {users?.pages?.map((page) =>
              page?.data?.map((user: User<true>) => {
                if (participantIds.has(user.id)) return null;

                const letter = getNameGroupLabel(user.first_name?.[0] ?? "#");
                const showHeader = letter !== prevLetter;
                const checked = selectedUsers.has(user.id);
                prevLetter = letter;

                return (
                  <div key={user.id}>
                    {showHeader && (
                      <div className="bg-gray-50 px-10 py-2 text-sm/5 font-medium text-gray-600">
                        {letter}
                      </div>
                    )}
                    <div
                      className="flex cursor-pointer items-center gap-3 rounded-lg px-6 py-2 hover:bg-gray-50"
                      onClick={() => handleToggleUser(user.id)}
                    >
                      <UserAvatar size="lg" user={user} disableTooltip />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-gray-700">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="truncate text-sm text-gray-400">
                          {user.position?.name || "-"}
                        </div>
                      </div>
                      <Checkbox checked={checked} onChange={() => {}} />
                    </div>
                  </div>
                );
              }),
            )}
          </InfiniteScroll>
        )}
      </div>

      <AddUsersConfirmModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirm}
        isLoading={isSubmitting}
        selectedCount={selectedUsers.size}
        userName={
          selectedUserList[0]
            ? `${selectedUserList[0].first_name} ${selectedUserList[0].last_name}`
            : undefined
        }
      />
    </div>
  );
}
